/**
 * Framing a sheet viewport on the live viewer: the floor isolated, a
 * top-down view, and a frame fixed in the middle of the screen that is
 * EXACTLY the area the viewport prints at the chosen scale. The user pans /
 * zooms the plan under it (normal viewer controls) and turns it with the
 * rotation buttons; confirming captures center, heading, scale and the view
 * settings (X-ray darkness, meters per screen pixel) into the sheet.
 *
 * The view lives in a "Frame" custom view on the main RenderView (snaps back
 * to the user's view on exit). Tools are switched off and a dark plane covers
 * the app: only the frame cutout and the framing bar take input.
 */

import { ArrowsPointingOutIcon, CheckIcon, MagnifyingGlassMinusIcon, MagnifyingGlassPlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo, useCallback, useContext, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { STANDARD_SCALES, formatScale, paperMmToWorld, type ViewportElement, type ViewportSettings } from 'shared-types';
import UndoDotIcon from '../../assets/icons/undo-dot.svg?react';
import { Button } from '../../components/Button';
import { RendererContext } from '../../contexts/RendererContext';
import { CameraAlignmentTool } from '../../potree/utils/CameraAlignmentTool';
import { CUSTOM_VIEW_ID_PREFIX } from '../../potree/viewer/RenderViewState';
import type { ITool } from '../../potree/personas/types';
import type { PrintRaster } from '../../potree/utils/PrintRasterRenderer';
import { T, useT } from '../../translation/src';
import { captureRasterLook, isRasterKind, previewDpi, renderViewportNow } from '../print/raster/viewportRasters';
import { enterViewportView, headingOfViewer, toRadians, type PrintViewer } from '../print/raster/viewerView';
import type { PageLines } from '../print/preview/pageLines';
import { frameAround, frameWorldSize, type PlanPoint } from './planFit';
import { RotationControl, normalizeHeading } from './RotationControl';

const OVERRIDE_OWNER = 'sheet-viewport-frame';
const FRAME_VIEW_ID = `${CUSTOM_VIEW_ID_PREFIX}sheet-frame`;
/** Raw English label: the view switcher translates it. */
const FRAME_VIEW_LABEL = 'Frame';
/** Share of the screen the frame takes when fitted. */
const FIT_SHARE = 0.75;

type FrameViewer = PrintViewer;

const renderAreaElement = () => document.getElementById('potree_render_area');

/** Center the view on `center` (or keep it) and zoom so a w × h m frame takes FIT_SHARE of the screen. */
function fitView(viewer: FrameViewer, center: { x: number; y: number } | null, w: number, h: number): void {
  const area = renderAreaElement();
  if (!area) return;
  const aspect = area.clientWidth / Math.max(1, area.clientHeight);
  const view = viewer.scene.view;
  viewer.cancelCameraMotion();
  const pivot = view.getPivot();
  const target = new THREE.Vector3(center?.x ?? pivot.x, center?.y ?? pivot.y, pivot.z);
  // Ortho frustum: half-width = radius, half-height = radius / aspect.
  const radius = Math.max(w / 2 / FIT_SHARE, (h / 2 / FIT_SHARE) * aspect);
  view.radius = radius;
  view.position.copy(target.clone().sub(view.direction.multiplyScalar(radius)));
}

/** Turns the top-down view: the override keeps it while the view is re-applied, the roll shows it now. */
function applyHeading(viewer: FrameViewer, override: OverrideHandle | null, headingDeg: number): void {
  override?.update(toRadians(-headingDeg));
  viewer.scene.view.roll = toRadians(-headingDeg);
}

/** World XY under a screen point of the (top-down, orthographic) view. */
function screenToWorld(viewer: FrameViewer, area: HTMLElement, clientX: number, clientY: number): { world: THREE.Vector3; screen: THREE.Vector2 } {
  const rect = area.getBoundingClientRect();
  const screen = new THREE.Vector2(clientX - rect.left, clientY - rect.top);
  const ndc = new THREE.Vector3((screen.x / rect.width) * 2 - 1, -(screen.y / rect.height) * 2 + 1, 0.5);
  const world = ndc.unproject(viewer.scene.getActiveCamera());
  world.z = viewer.scene.view.getPivot().z;
  return { world, screen };
}

/**
 * Two clicks on the plan (along a wall) report that line; the viewer's own
 * mouse handling is held off meanwhile (wheel zoom still works). The shared
 * CameraAlignmentTool draws the line and its angle. Esc is handled by the
 * overlay's key blocker. Returns the cleanup.
 */
function pickAlignmentLine(viewer: FrameViewer, onLine: (a: THREE.Vector3, b: THREE.Vector3) => void): () => void {
  const area = renderAreaElement();
  if (!area) return () => undefined;
  const tool = new CameraAlignmentTool(viewer);
  let first: THREE.Vector3 | null = null;
  const inside = (e: Event) => e.target instanceof Node && area.contains(e.target);
  const block = (e: MouseEvent) => {
    if (!inside(e) || e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
  };
  const down = (e: PointerEvent) => {
    if (!inside(e) || e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    const { world, screen } = screenToWorld(viewer, area, e.clientX, e.clientY);
    if (!first) {
      first = world;
      tool.start(world, screen);
    } else {
      tool.cancel();
      onLine(first, world);
    }
  };
  const move = (e: PointerEvent) => {
    if (!first || !inside(e)) return;
    const { world, screen } = screenToWorld(viewer, area, e.clientX, e.clientY);
    tool.updateEndPoint(world, screen);
  };
  window.addEventListener('pointerdown', down, true);
  window.addEventListener('mousedown', block, true);
  window.addEventListener('click', block, true);
  window.addEventListener('pointermove', move, true);
  const previousCursor = area.style.cursor;
  area.style.cursor = 'crosshair';
  return () => {
    window.removeEventListener('pointerdown', down, true);
    window.removeEventListener('mousedown', block, true);
    window.removeEventListener('click', block, true);
    window.removeEventListener('pointermove', move, true);
    area.style.cursor = previousCursor;
    tool.cancel();
  };
}

/** The heading that lays the line a→b along the page's x or y axis, whichever is the smallest turn from `current`. */
function headingAlongLine(a: THREE.Vector3, b: THREE.Vector3, current: number): number {
  const angle = (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
  let best = angle;
  for (let k = -4; k <= 4; k++) {
    const candidate = angle + k * 90;
    if (Math.abs(normalizeHeading(candidate - current)) < Math.abs(normalizeHeading(best - current))) best = candidate;
  }
  return normalizeHeading(best);
}

interface OverrideHandle {
  update: (heading: number) => void;
  release: () => void;
}

/**
 * Switches off every enabled tool (and click selection) so the viewer only
 * navigates; returns the function that switches them back on. Goes around
 * the persona's enable/disableTool on purpose: those re-pick the available
 * views and would pull the viewer out of the framing view.
 */
function suspendTools(viewer: FrameViewer): () => void {
  const tools: ITool[] = (viewer.personaManager?.getActivePersonas() ?? []).flatMap((persona) => persona.getEnabledTools());
  tools.forEach((tool) => tool.disable());
  const input = viewer.inputHandler;
  const selectionWasDisabled = input?.selectionDisabled ?? false;
  if (input) input.selectionDisabled = true;
  return () => {
    if (input) input.selectionDisabled = selectionWasDisabled;
    tools.forEach((tool) => tool.enable());
  };
}

/**
 * Puts the viewer in framing mode (tools off, floor isolated, top-down view
 * in a "Frame" custom view, heading override) and returns the function that
 * puts everything back.
 */
function enterFraming(
  viewer: FrameViewer,
  options: { floorId: string | null; kind: ViewportSettings['kind']; /** null = keep the view's own rotation */ headingDeg: number | null; onReady: (handle: OverrideHandle, headingDeg: number) => void; fit: () => void },
): () => void {
  const resumeTools = suspendTools(viewer);
  const mainState = viewer.renderViews[0]?.viewState ?? null;
  // The view to return to, captured before the switch below (like wall focus): the
  // custom view snaps back to this pose and view on exit.
  const returnViewId = viewer.personaManager?.viewManager.getCurrentView()?.id ?? null;
  const origin = mainState?.captureTransientState() ?? null;
  if (origin) origin.currentViewId = returnViewId;
  const view = enterViewportView(viewer, { floorId: options.floorId, kind: options.kind });
  let override: OverrideHandle | null = null;
  void view.ready.then((ready) => {
    if (!ready) return;
    if (mainState && origin) {
      // The tab starts out as a copy of `origin` (the view before framing) and activating it applies that:
      // put the X-ray 2D state the switch just installed back on top.
      const framing = mainState.captureTransientState();
      mainState.createOrActivateCustomView({ id: FRAME_VIEW_ID, label: FRAME_VIEW_LABEL, persistent: true, origin });
      mainState.restoreTransientState({ ...framing, currentViewId: FRAME_VIEW_ID });
    }
    const headingDeg = options.headingDeg ?? headingOfViewer(viewer);
    override = viewer.cameraOrientationManager.pushOverride(OVERRIDE_OWNER, 'top_down_2d', toRadians(-headingDeg));
    applyHeading(viewer, override, headingDeg);
    options.onReady(override, headingDeg);
    options.fit();
  });
  return () => {
    override?.release();
    if (mainState?.getCustomView(FRAME_VIEW_ID)) {
      // Leave through setView: it restores the origin pose and syncs the global view; then drop the tab.
      if (returnViewId && mainState.currentViewId.value === FRAME_VIEW_ID) mainState.setView(returnViewId);
      mainState.removeCustomView(FRAME_VIEW_ID);
    }
    view.release();
    resumeTools();
  };
}

export interface FrameResult extends Pick<ViewportSettings, 'xray' | 'heatmap' | 'screenMetersPerPixel'> {
  center: { x: number; y: number };
  headingDeg: number;
  scaleDen: number;
  viewMode: string | null;
  /** Raster kinds: a quick render of the framed area (what is loaded now) for the sheet preview. */
  preview: PrintRaster | null;
}

interface ReframeOverlayProps {
  element: ViewportElement;
  settings: ViewportSettings;
  /** Floor to isolate (the viewport's floor). */
  floorId: string | null;
  /** The floor's drawing (wall sketch, rooms, objects): fit puts the frame around it; a never-framed view starts there. */
  fitPoints: readonly PlanPoint[];
  /** The page's linework around the viewport (title block, borders…), drawn over the view while framing. */
  pageLines?: PageLines | null;
  onConfirm: (frame: FrameResult) => void;
  onCancel: () => void;
}

const ReframeOverlay_: React.FC<ReframeOverlayProps> = ({ element, settings, floorId, fitPoints, pageLines, onConfirm, onCancel }) => {
  const { viewer } = useContext(RendererContext);
  const [scaleDen, setScaleDen] = useState(settings.scaleDen);
  const [heading, setHeading] = useState(settings.capturedAt ? settings.headingDeg : 0);
  /** The heading override is in place (before that, turning has nothing to turn). */
  const [ready, setReady] = useState(false);
  const [frame, setFrame] = useState<{ left: number; top: number; width: number; height: number } | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [aligning, setAligning] = useState(false);
  const overrideRef = useRef<OverrideHandle | null>(null);
  const hint = useT('move and zoom the plan under the frame; what is inside prints', { swc: true });
  const alignHint = useT('click two points along a wall: the plan turns to make it straight (Esc cancels)', { swc: true });
  const labels = { align: useT('align to a wall', { swc: true }), fit: useT('fit the frame around the drawing', { swc: true }) };

  const worldW = paperMmToWorld(element.wMm, scaleDen);
  const worldH = paperMmToWorld(element.hMm, scaleDen);

  const fitTo = useCallback((center: { x: number; y: number } | null, w: number, h: number) => viewer && fitView(viewer, center, w, h), [viewer]);

  // Enter: isolate the floor, switch to a top-down view, restore heading / center. Leave: put everything back.
  useEffect(() => {
    if (!viewer) return;
    return enterFraming(viewer, {
      floorId,
      kind: settings.kind,
      headingDeg: settings.capturedAt ? settings.headingDeg : null,
      onReady: (handle, headingDeg) => {
        overrideRef.current = handle;
        setHeading(headingDeg);
        setReady(true);
      },
      fit: () => fitView(viewer, settings.capturedAt ? settings.center : (frameAround(fitPoints, settings.headingDeg, element)?.center ?? null), worldW, worldH),
    });
    // Entering is a one-off for this overlay's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewer]);

  // Turning: the plan rotates under the frame.
  useEffect(() => {
    if (!ready || !viewer) return;
    applyHeading(viewer, overrideRef.current, heading);
  }, [heading, ready, viewer]);

  // The frame follows zoom: its size on screen is the print area at the current meters-per-pixel.
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const area = renderAreaElement();
      if (viewer && area) {
        const rect = area.getBoundingClientRect();
        const metersPerPixel = (2 * viewer.scene.view.radius) / Math.max(1, rect.width);
        const width = worldW / metersPerPixel;
        const height = worldH / metersPerPixel;
        setFrame((f) => {
          const next = { left: rect.left + (rect.width - width) / 2, top: rect.top + (rect.height - height) / 2, width, height };
          return f && Math.abs(f.left - next.left) < 0.5 && Math.abs(f.top - next.top) < 0.5 && Math.abs(f.width - next.width) < 0.5 ? f : next;
        });
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [viewer, worldW, worldH]);

  // A new scale keeps the frame's size on screen: the view zooms around its center so the new print area fits.
  const changeScale = (next: number) => {
    setScaleDen(next);
    const size = frameWorldSize(element, next);
    fitTo(null, size.w, size.h);
  };

  const stepScale = (direction: 1 | -1) => {
    const index = STANDARD_SCALES.indexOf(scaleDen);
    const base = index >= 0 ? index : STANDARD_SCALES.findIndex((s) => s > scaleDen) - (direction > 0 ? 1 : 0);
    const next = STANDARD_SCALES[Math.min(STANDARD_SCALES.length - 1, Math.max(0, base + direction))];
    changeScale(next);
  };

  const confirm = async () => {
    if (!viewer) return;
    const view = viewer.scene.view;
    const pivot = view.getPivot();
    const frame = { center: { x: Math.round(pivot.x * 1000) / 1000, y: Math.round(pivot.y * 1000) / 1000 }, headingDeg: headingOfViewer(viewer), scaleDen };
    // The look the user sees now (X-ray darkness at this pixel size, heatmap): the print reproduces it at any dpi.
    const look = captureRasterLook(viewer, element, settings, frame);
    let preview: PrintRaster | null = null;
    if (isRasterKind(settings.kind)) {
      setCapturing(true);
      try {
        preview = await renderViewportNow(viewer, { key: '', box: element, settings: { ...settings, ...frame, ...look } }, { dpi: previewDpi(element, settings.dpi), load: 'none' });
      } catch (error) {
        console.error('Viewport preview could not be rendered', error);
      } finally {
        setCapturing(false);
      }
    }
    onConfirm({ ...frame, ...look, viewMode: viewer.personaManager?.viewManager.getCurrentView()?.id ?? null, preview });
  };

  // Fit: the frame around the drawing at the current rotation — the most detailed standard scale it fits at, centered.
  const fitToDrawing = () => {
    if (!viewer) return;
    const fit = frameAround(fitPoints, heading, element);
    if (!fit) {
      fitTo(null, worldW, worldH);
      return;
    }
    setScaleDen(fit.scaleDen);
    const size = frameWorldSize(element, fit.scaleDen);
    fitTo(fit.center, size.w, size.h);
  };

  // Align: two clicks along a wall straighten the plan.
  useEffect(() => {
    if (!aligning || !viewer) return;
    return pickAlignmentLine(
      viewer,
      (a, b) => {
        setHeading((current) => headingAlongLine(a, b, current));
        setAligning(false);
      },
    );
  }, [aligning, viewer]);

  // Keys: only the framing bar gets them; the app's shortcuts and tools stay quiet. Esc stops aligning, else cancels.
  const barRef = useRef<HTMLDivElement>(null);
  const escapeRef = useRef<() => void>(onCancel);
  useEffect(() => {
    escapeRef.current = aligning ? () => setAligning(false) : onCancel;
  }, [aligning, onCancel]);
  useEffect(() => {
    const block = (e: KeyboardEvent) => {
      if (e.target instanceof Node && barRef.current?.contains(e.target)) return;
      e.stopPropagation();
      if (e.type === 'keydown' && e.key === 'Escape') escapeRef.current();
    };
    window.addEventListener('keydown', block, true);
    window.addEventListener('keyup', block, true);
    return () => {
      window.removeEventListener('keydown', block, true);
      window.removeEventListener('keyup', block, true);
    };
  }, []);

  return (
    <>
      {frame && (
        <div className="pointer-events-none fixed inset-0 z-[60] overflow-hidden">
          {/* The dark plane: covers and blocks the whole app around the frame. Only the cutout lets input through to the viewer. */}
          {[
            { left: 0, top: 0, right: 0, height: Math.max(0, frame.top) },
            { left: 0, top: frame.top + frame.height, right: 0, bottom: 0 },
            { left: 0, top: frame.top, width: Math.max(0, frame.left), height: frame.height },
            { left: frame.left + frame.width, top: frame.top, right: 0, height: frame.height },
          ].map((style, index) => (
            <div key={index} className="pointer-events-auto absolute bg-black/50" style={style} onContextMenu={(e) => e.preventDefault()} />
          ))}
          <div className="absolute border-2 border-french-violet-600 dark:border-neon-green-300" style={{ left: frame.left, top: frame.top, width: frame.width, height: frame.height }}>
            <span className="absolute -top-6 left-0 rounded bg-french-violet-600 px-2 py-0.5 text-xs text-white dark:bg-neon-green-300 dark:text-black">
              {element.label || element.id} · {formatScale(scaleDen)} · {element.wMm} × {element.hMm} mm
            </span>
            {pageLines && (
              // The rest of the page, lined up with the frame: where the title block, arrows and borders will sit over the plan.
              <div
                aria-hidden
                className="pointer-events-none absolute opacity-80 [filter:drop-shadow(0_0_1px_rgba(0,0,0,0.9))] [&>svg]:h-full [&>svg]:w-full [&_path]:!stroke-white"
                style={{
                  // Positioned from inside the frame's 2 px border.
                  left: -2 - pageLines.viewportXmm * (frame.width / element.wMm),
                  top: -2 - pageLines.viewportYmm * (frame.width / element.wMm),
                  width: pageLines.pageWidthMm * (frame.width / element.wMm),
                  height: pageLines.pageHeightMm * (frame.width / element.wMm),
                }}
                dangerouslySetInnerHTML={{ __html: pageLines.svg }}
              />
            )}
          </div>
        </div>
      )}
      <div ref={barRef} className="fixed bottom-6 left-1/2 z-[61] flex -translate-x-1/2 flex-col items-center gap-2">
        <span className="rounded bg-black/70 px-3 py-1 text-xs text-white">{aligning ? alignHint : hint}</span>
        <div className="flex max-w-[calc(100vw-2rem)] flex-wrap items-center justify-center gap-2 rounded-xl bg-white p-2 shadow-lg dark:bg-[#262626]">
          <button type="button" className="rounded p-1.5 hover:bg-black/10" onClick={() => stepScale(1)} title="1:larger">
            <MagnifyingGlassMinusIcon className="h-5 w-5" />
          </button>
          <select value={scaleDen} onChange={(e) => changeScale(Number(e.target.value))} className="h-8 rounded-md border border-gray-300 py-0 pl-2 pr-7 text-sm dark:border-gray-600 dark:bg-[#262626]">
            {[...new Set([...STANDARD_SCALES, scaleDen])]
              .sort((a, b) => a - b)
              .map((s) => (
                <option key={s} value={s}>
                  {formatScale(s)}
                </option>
              ))}
          </select>
          <button type="button" className="rounded p-1.5 hover:bg-black/10" onClick={() => stepScale(-1)} title="1:smaller">
            <MagnifyingGlassPlusIcon className="h-5 w-5" />
          </button>
          <span className="mx-1 h-6 w-px bg-gray-300" />
          <RotationControl value={heading} onChange={setHeading} />
          <button
            type="button"
            className={classNames('rounded p-1.5 hover:bg-black/10 dark:hover:bg-white/10', aligning && 'bg-french-violet-600 text-white hover:bg-french-violet-600 dark:bg-neon-green-300 dark:text-black')}
            onClick={() => setAligning((a) => !a)}
            title={labels.align}
            aria-label={labels.align}
            aria-pressed={aligning}
          >
            <UndoDotIcon className="h-5 w-5" />
          </button>
          <span className="mx-1 h-6 w-px bg-gray-300" />
          <button type="button" className="rounded p-1.5 hover:bg-black/10 dark:hover:bg-white/10" onClick={fitToDrawing} title={labels.fit} aria-label={labels.fit}>
            <ArrowsPointingOutIcon className="h-5 w-5" />
          </button>
          <Button variant="secondary" size="xs" onClick={onCancel}>
            <XMarkIcon className="mr-1 w-4" />
            <span className="whitespace-nowrap">
              <T _str="cancel" swc />
            </span>
          </Button>
          <Button variant="primary" size="xs" onClick={() => void confirm()} loading={capturing}>
            <CheckIcon className="mr-1 w-4" />
            <span className="whitespace-nowrap">
              <T _str="use this framing" swc />
            </span>
          </Button>
        </div>
      </div>
    </>
  );
};

export const ReframeOverlay = memo(ReframeOverlay_);
